import { useState, useEffect, useContext } from 'react';
import { useLocation, useParams, Link } from 'react-router-dom';
import { updateClient, getClientById, sendNewClientForm, UserContext, createClientUpdate, convertNestedEmptyToNull } from '../services/firebase';
import { Client, Currency } from '../shared/types';
import { Controller, useForm } from 'react-hook-form';
import * as Sentry from '@sentry/react';
import { grailCurrencies } from '../shared/currency';

// Components
import CountrySelect from '../components/CountrySelect';
import LayoutOnboarding from '../components/LayoutOnboarding';
import SectorSelect from '../components/SectorSelect';
import { Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FormCheckbox, FormCheckboxGroup } from '@/components/forms/FormCheckbox';
import { FormInput } from '@/components/forms/FormInput';
import { FormSelect } from '@/components/forms/FormSelect';
import { Field, FieldDescription, FieldGroup, FieldLabel, FieldSeparator } from '@/components/ui/field';
import { SelectItem } from '@/components/ui/select';
import { toast } from '@/components/ui/toast';
import { FormSkeleton } from '@/components/FormSkeleton';
import { useSkeletonPhases } from '@/hooks/useSkeletonPhases';
import { cn } from "cn";

type EditClientForm = {
    name?: string;
    nameRegistered?: string;
    nameTrading?: string;
    address?: {
        addressLine1?: string;
        addressLine2?: string;
        city?: string;
        state?: string;
        postCode?: string;
        country?: string | { label?: string };
    };
    billingEmail?: string;
    billingPhone?: string;
    companySector?: Client["companySector"] | "";
    orgNumber?: string;
    vatNumber?: string;
    clientWideSettings?: {
        paymentTerms?: {
            termsAccepted?: boolean;
            firstName?: string;
            lastName?: string;
        };
        currency?: Currency | "";
    };
    batchInvoices?: boolean;
    netPaymentTerms?: string | number;
    hidden?: boolean;
    requiresInvoiceNotes?: boolean;
    requiresPurchaseOrder?: boolean;
    requiresUniportEmail?: boolean;
    invoiceBothBookerAndClient?: boolean;
};

/** Viewport-of-the-card, not the true field count. Extra `"input"`s fill the content area below chrome. */
const EDIT_CLIENT_SKELETON_FIELDS = Array.from({ length: 10 }, () => "input" as const);

export function clientCurrencyLock(managerMode: boolean, currency?: Currency | "") {
    if (!managerMode) {
        return {};
    }
    return { currency: currency === "USD" || currency === "GBP" ? currency : null };
}

function EditClient(props: { managerMode?: boolean; adminMode?: boolean }) {
    const { clientId } = useParams<{ clientId: string }>();
    const [client, setClient] = useState<Client>();
    const [submissionStatus, setSubmissionStatus] = useState<'default' | 'pending' | 'success' | 'error'>('default');
    const [errorCode, setErrorCode] = useState('');
    const { register, control, handleSubmit } = useForm<EditClientForm>(); // , formState: { errors }
    const [modalOpen, setModalOpen] = useState(false);
    const [clientSendStatus, setClientSendStatus] = useState<'default' | 'pending' | 'error'>('default');
    const user = useContext(UserContext);
    const location = useLocation();

    const managerMode = props.managerMode || user?.roles?.manager;
    const adminMode = props.adminMode || user?.roles?.admin;
    const phase = useSkeletonPhases(Boolean(managerMode && !client));

    useEffect(() => {
        if (managerMode) {
            getInitialProps();
        } else {
            setClient({ id: clientId, name: '' });
        }
        async function getInitialProps() {
            if (clientId) {
                const newClient = await getClientById(clientId);
                setClient(newClient);
            }
        }
    }, [clientId, props, managerMode]);

    async function submitForm(formData: EditClientForm) {
        console.log(formData);

        if (!clientId) {
            return;
        }

        const addressCountry = formData.address?.country;
        const settingsCurrency = formData.clientWideSettings?.currency;
        const clientWideSettings = {
            ...(formData.clientWideSettings?.paymentTerms?.termsAccepted && {
                paymentTerms: {
                    termsAccepted: formData.clientWideSettings.paymentTerms.termsAccepted,
                    firstName: formData.clientWideSettings.paymentTerms.firstName,
                    lastName: formData.clientWideSettings.paymentTerms.lastName,
                    dateAccepted: new Date(),
                },
            }),
            ...clientCurrencyLock(Boolean(managerMode), settingsCurrency),
            ...(adminMode && {
                batchInvoices: formData.batchInvoices ? true : false,
            }),
        };

        const newClient = {
            name: formData.name,
            nameRegistered: formData.nameRegistered,
            nameTrading: formData.nameTrading,
            address: {
                addressLine1: formData.address?.addressLine1,
                addressLine2: formData.address?.addressLine2,
                city: formData.address?.city,
                state: formData.address?.state,
                postCode: formData.address?.postCode,
                country: (typeof addressCountry === "object" && addressCountry?.label) || (typeof addressCountry === "string" ? addressCountry : ""),
            },
            billingEmail: formData.billingEmail,
            billingPhone: formData.billingPhone,
            companySector: formData.companySector,
            orgNumber: formData.orgNumber,
            vatNumber: formData.vatNumber,
            ...(Object.keys(clientWideSettings).length > 0 ? { clientWideSettings } : {}),
            ...(managerMode && {
                netPaymentTerms: parseInt(String(formData.netPaymentTerms || 0)),
            }),
            ...(adminMode && {
                hidden: Boolean(formData.hidden),
                requiresInvoiceNotes: Boolean(formData.requiresInvoiceNotes),
                requiresPurchaseOrder: Boolean(formData.requiresPurchaseOrder),
                requiresUniportEmail: Boolean(formData.requiresUniportEmail),
                invoiceBothBookerAndClient: formData.invoiceBothBookerAndClient !== false,
            }),
        };

        console.log(`NEW CLIENT`, newClient);

        const res = await updateClient(createClientUpdate({
            id: clientId,
            ...convertNestedEmptyToNull(newClient),
        }));

        if (res.success) {
            if (managerMode) {
                toast.add({
                    type: 'success',
                    title: 'Changes saved',
                    description: 'Client info has been saved.',
                });
                setSubmissionStatus('default');
            } else {
                setSubmissionStatus('success');
            }
        } else {
            if (res.error.code === 'permission-denied') {
                setErrorCode('previously-completed');
            } else {
                setErrorCode(res.error.code);
                Sentry.captureException(res.error);
            }

            setSubmissionStatus('error');
        }
    }

    async function sendToClient(event: React.SyntheticEvent) {
        event.preventDefault();
        const target = event.target as typeof event.target & {
            email: { value: string };
        };
        console.log(target.email.value);
        setClientSendStatus('pending');

        const res = await sendNewClientForm(clientId || '', target.email.value);

        if (res.success) {
            toast.add({
                type: 'success',
                title: 'Form sent',
                description: 'Form was successfully sent!',
            });
            handleModalClose();
        } else {
            setClientSendStatus('error');
            console.log(res.error);
        }
    }

    function copyClientLink(clientId: string) {
        navigator.clipboard.writeText(`https://app.grail-talent.com/new-client/${clientId}`);
        toast.add({
            type: 'success',
            title: 'Link copied',
        });
    }

    function handleModalClose() {
        setClientSendStatus('default');
        setModalOpen(false);
    }

    if (managerMode && location.pathname.includes('/new-client/')) {
        console.log(`Show other thing`);

        return (
            <LayoutOnboarding>
                <div>
                    This form is only for use by new clients.
                    {client?.name ? (
                        <>
                            <p>
                                To update the details for {client.name}, <Link to={`/edit-client/${client.id}`}>click here</Link>.
                            </p>{' '}
                            <p className="text-lg">
                                <u>
                                    Do <strong>NOT</strong>
                                </u>{' '}
                                use the link above to create a new client!
                            </p>
                        </>
                    ) : null}
                </div>
            </LayoutOnboarding>
        );
    }

    return (
        <div>
            <Dialog
                open={modalOpen}
                onOpenChange={(nextOpen) => {
                    if (!nextOpen) handleModalClose();
                    else setModalOpen(true);
                }}
            >
                <DialogContent className="sm:max-w-md" showCloseButton={false}>
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-semibold">Send New Client Form to Client</DialogTitle>
                    </DialogHeader>

                    {clientSendStatus === 'pending' ? (
                        <div className="text-center mt-12 mb-12">
                            <Loader2 className="size-8 animate-spin text-primary mx-auto" />
                        </div>
                    ) : null}

                    {clientSendStatus === 'default' || clientSendStatus === 'error' ? (
                        <>
                            {clientSendStatus === 'error' ? (
                                <>
                                    <Alert className="mt-4 mb-4 text-left" variant="destructive">
                                        <AlertDescription>Something went wrong. Please try again.</AlertDescription>
                                    </Alert>
                                </>
                            ) : null}
                            <form onSubmit={(e) => sendToClient(e)}>
                                <FieldGroup>
                                    <FormInput label="Client Email Address" name="email" type="email" required />

                                    <Button type="submit">Send Form</Button>
                                </FieldGroup>
                            </form>
                        </>
                    ) : null}
                </DialogContent>
            </Dialog>

            <LayoutOnboarding>
                {clientId === 'undefined' || clientId === 'null' ? (
                    <>This link is invalid. If you think this is an error, please contact Grail with this URL and a description of how you arrived here.</>
                ) : null}
                {managerMode && clientId && clientId !== 'undefined' && clientId !== 'null' ? (
                    <div className="mb-4">
                        <div className="mt-2 mb-2">
                            <Button size="sm" onClick={() => setModalOpen(true)}>
                                Send New Client Form to Client
                            </Button>
                        </div>
                        <div className="mt-2 mb-2">
                            <Button size="sm" variant="contrast" onClick={() => copyClientLink(clientId)}>
                                Copy Link to Client Form
                            </Button>
                        </div>
                    </div>
                ) : null}
                {phase === "showSkeleton" ? (
                    <Card size="sm" className="mx-auto w-100 max-w-full">
                        <CardContent>
                            <FormSkeleton fields={EDIT_CLIENT_SKELETON_FIELDS} />
                        </CardContent>
                    </Card>
                ) : clientId && clientId !== 'undefined' && clientId !== 'null' && client ? (
                    <>
                        {submissionStatus === 'pending' ? (
                            <div className="text-center mt-12 mb-12">
                                <Loader2 className="size-8 animate-spin text-primary mx-auto" />
                            </div>
                        ) : null}

                        {/* JUST HIDE IT when not 'default' or 'error', don't destroy it, in order to preserve form state */}
                        <form
                            className={cn(
                                'mx-auto w-100 max-w-full text-left',
                                submissionStatus !== 'default' && submissionStatus !== 'error' && 'hidden'
                            )}
                            onSubmit={handleSubmit(submitForm)}
                        >
                            <Card size="sm">
                                <CardContent>
                                    <FieldGroup>
                                        {
                                            !managerMode ? (
                                                <div>
                                                    <FormInput label="First Name" required type="text" {...register('clientWideSettings.paymentTerms.firstName', { required: true })} />
                                                    <FormInput label="Last Name" required type="text" {...register('clientWideSettings.paymentTerms.lastName', { required: true })} />

                                                    <FieldSeparator />
                                                </div>
                                            ) : null
                                            // (
                                            //     <div className="mb-12 text-left">
                                            //         <strong>NOTE:</strong> Only clients and admins can edit client details. Think something needs changing? <a className="alternate" href={`mailto:lyonel@grail-talent.com?subject=${client?.name} Client Info Inquiry`} target="_blank" rel="noreferrer">Click here</a> to mail the operations team
                                            //     </div>
                                            // )
                                        }

                                        <FormInput label="Company Name" required type="text" {...register('name', { required: true, value: client?.name })} />

                                        <FormInput
                                            label="Registered Name (If Different)"
                                            type="text"
                                            {...register('nameRegistered', {
                                                value: client?.nameRegistered,
                                            })}
                                        />

                                        <FormInput label="Trading Name (If Different)" type="text" {...register('nameTrading', { value: client?.nameTrading })} />

                                        <FieldSeparator />

                                        <Field>
                                            <FieldLabel>
                                                Company Sector <span className="text-destructive">*</span>
                                            </FieldLabel>
                                            <Controller
                                                control={control}
                                                name="companySector"
                                                defaultValue={client?.companySector || ''}
                                                render={({ field: { onChange, value } }) => <SectorSelect onChange={onChange} value={value} required={true} />}
                                            />
                                        </Field>

                                        <FieldSeparator />

                                        <FormInput
                                            label="Address Line 1"
                                            required
                                            type="text"
                                            {...register('address.addressLine1', { required: true, value: client?.address?.addressLine1 })}
                                            readOnly={Boolean(managerMode && !adminMode && client?.address?.addressLine1)}
                                        />

                                        <FormInput
                                            label="Address Line 2"
                                            type="text"
                                            {...register('address.addressLine2', {
                                                value: client?.address?.addressLine2,
                                            })}
                                            readOnly={Boolean(managerMode && !adminMode && client?.address?.addressLine2)}
                                        />

                                        <FormInput
                                            label="City/Town"
                                            required
                                            type="text"
                                            {...register('address.city', { required: true, value: client?.address?.city })}
                                            readOnly={Boolean(managerMode && !adminMode && client?.address?.city)}
                                        />

                                        <FormInput
                                            label="State/Region"
                                            required
                                            type="text"
                                            {...register('address.state', { required: true, value: client?.address?.state })}
                                            readOnly={Boolean(managerMode && !adminMode && client?.address?.state)}
                                        />

                                        <FormInput
                                            label="Zip/Postal Code"
                                            required
                                            type="text"
                                            {...register('address.postCode', { required: true, value: client?.address?.postCode })}
                                            readOnly={Boolean(managerMode && !adminMode && client?.address?.postCode)}
                                        />

                                        <Field>
                                            <FieldLabel>
                                                Country <span className="text-destructive">*</span>
                                            </FieldLabel>
                                            <Controller
                                                control={control}
                                                name="address.country"
                                                defaultValue={client?.address?.country || ''}
                                                render={({ field: { onChange, value } }) => (
                                                    <CountrySelect onChange={onChange} value={value} required={true} readOnly={Boolean(managerMode && !adminMode && client?.address?.country)} />
                                                )}
                                            />

                                            {managerMode && !adminMode && client?.address?.country ? (
                                                <FieldDescription>
                                                    To prevent billing issues, once completed client addresses can only be changed by an admin. Contact lyonel@grail-talent.com for more info.
                                                </FieldDescription>
                                            ) : null}
                                        </Field>

                                        <FieldSeparator />

                                        <FormInput
                                            label="Billing/Finance Dep. Email"
                                            required
                                            description="If you don't have a billing department, enter your own info below instead."
                                            type="email"
                                            {...register('billingEmail', { required: true, value: client?.billingEmail })}
                                        />

                                        <FormInput
                                            label="Billing/Finance Dep. Phone Number"
                                            required={!managerMode}
                                            type="text"
                                            {...register('billingPhone', {
                                                required: managerMode ? false : true,
                                                value: client?.billingPhone,
                                            })}
                                        />

                                        <FieldSeparator />

                                        <FormInput label="VAT Number (UK/EU Only)" type="text" {...register('vatNumber', { value: client?.vatNumber })} />

                                        <FormInput label="Organization Number (if different)" type="text" {...register('orgNumber', { value: client?.orgNumber })} />

                                        {!managerMode ? (
                                            <Controller
                                                control={control}
                                                name="clientWideSettings.paymentTerms.termsAccepted"
                                                rules={{ required: true }}
                                                render={({ field }) => (
                                                    <FormCheckbox
                                                        label={
                                                            <>
                                                                Do you agree to the{' '}
                                                                <a href="https://grail-talent.com/payment-terms" target="_blank" rel="noreferrer">
                                                                    Grail Talent Payment terms
                                                                </a>
                                                                ?
                                                            </>
                                                        }
                                                        required
                                                        checked={Boolean(field.value)}
                                                        onChange={field.onChange}
                                                    />
                                                )}
                                            />
                                        ) : null}

                                        {managerMode ? (
                                            <>
                                                <FieldSeparator />
                                                <Controller
                                                    control={control}
                                                    name="clientWideSettings.currency"
                                                    defaultValue={client?.clientWideSettings?.currency || ''}
                                                    render={({ field }) => (
                                                        <FormSelect
                                                            label="Lock Currency"
                                                            value={field.value ? field.value : '__any__'}
                                                            onChange={(value) =>
                                                                field.onChange(!value || value === '__any__' ? '' : value)
                                                            }
                                                            placeholder="Any Currency Allowed"
                                                        >
                                                            <SelectItem value="__any__">Any Currency Allowed</SelectItem>
                                                            {grailCurrencies.map((currency) => (
                                                                <SelectItem key={currency.code} value={currency.code}>
                                                                    {currency.label}
                                                                </SelectItem>
                                                            ))}
                                                        </FormSelect>
                                                    )}
                                                />

                                                <FormCheckboxGroup
                                                    label="Approval Requirements"
                                                    description={!adminMode ? 'Editable by admins only' : undefined}
                                                    disabled={!adminMode}
                                                >
                                                    <Controller
                                                        control={control}
                                                        name="requiresInvoiceNotes"
                                                        defaultValue={Boolean(client?.requiresInvoiceNotes)}
                                                        render={({ field }) => (
                                                            <FormCheckbox
                                                                label="Requires Invoice Notes"
                                                                checked={Boolean(field.value)}
                                                                onChange={field.onChange}
                                                                disabled={!adminMode}
                                                            />
                                                        )}
                                                    />
                                                    <Controller
                                                        control={control}
                                                        name="requiresPurchaseOrder"
                                                        defaultValue={Boolean(client?.requiresPurchaseOrder)}
                                                        render={({ field }) => (
                                                            <FormCheckbox
                                                                label="Requires Purchase Order"
                                                                checked={Boolean(field.value)}
                                                                onChange={field.onChange}
                                                                disabled={!adminMode}
                                                            />
                                                        )}
                                                    />
                                                    <Controller
                                                        control={control}
                                                        name="requiresUniportEmail"
                                                        defaultValue={Boolean(client?.requiresUniportEmail)}
                                                        render={({ field }) => (
                                                            <FormCheckbox
                                                                label="Requires Uniport Email"
                                                                checked={Boolean(field.value)}
                                                                onChange={field.onChange}
                                                                disabled={!adminMode}
                                                            />
                                                        )}
                                                    />
                                                </FormCheckboxGroup>

                                                <FieldSeparator />

                                                <FormInput
                                                    label="Payment Terms"
                                                    type="number"
                                                    {...register('netPaymentTerms', {
                                                        value: client?.netPaymentTerms,
                                                    })}
                                                />

                                                <FieldSeparator />
                                            </>
                                        ) : null}

                                        {adminMode ? (
                                            <>
                                                <FormCheckboxGroup label="Admin Settings">
                                                    <Controller
                                                        control={control}
                                                        name="hidden"
                                                        defaultValue={Boolean(client?.hidden)}
                                                        render={({ field }) => (
                                                            <FormCheckbox
                                                                label="Hide Client from New Bookings Flow"
                                                                checked={Boolean(field.value)}
                                                                onChange={field.onChange}
                                                            />
                                                        )}
                                                    />
                                                    <Controller
                                                        control={control}
                                                        name="batchInvoices"
                                                        defaultValue={Boolean(client?.clientWideSettings?.batchInvoices)}
                                                        render={({ field }) => (
                                                            <FormCheckbox
                                                                label="Batch Invoices"
                                                                checked={Boolean(field.value)}
                                                                onChange={field.onChange}
                                                            />
                                                        )}
                                                    />
                                                </FormCheckboxGroup>

                                                <Controller
                                                    control={control}
                                                    name="invoiceBothBookerAndClient"
                                                    defaultValue={client?.invoiceBothBookerAndClient ?? true}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Invoice Both Booker and Client"
                                                            description="Send Invoice to Both Billing Email and Booker Email"
                                                            checked={Boolean(field.value)}
                                                            onChange={field.onChange}
                                                        />
                                                    )}
                                                />
                                            </>
                                        ) : null}

                                        {submissionStatus === 'error' ? (
                                            <>
                                                {errorCode === 'previously-completed' ? (
                                                    <Alert className="mt-4 mb-4 text-left">
                                                        <AlertDescription>
                                                            <div className="mb-4">Another user has already completed this form.</div>
                                                            <div className="mb-4">
                                                                If Grail already has your company info, you can safely close this form. If you think this is an error, please contact your Grail account
                                                                manager.
                                                            </div>
                                                        </AlertDescription>
                                                    </Alert>
                                                ) : (
                                                    <Alert className="mt-4 mb-4 text-left" variant="destructive">
                                                        <AlertDescription>
                                                            <div className="mb-4">An error occured. Please try submitting the form again.</div>
                                                            <div>
                                                                If this error persists, please contact your Grail account manager and provide this code: <strong>{errorCode}</strong>
                                                            </div>
                                                        </AlertDescription>
                                                    </Alert>
                                                )}
                                            </>
                                        ) : null}

                                        <Button type="submit" className="mt-8 w-full">
                                            {managerMode ? 'Save Client Info' : 'Save My Info'}
                                        </Button>
                                    </FieldGroup>
                                </CardContent>
                            </Card>
                        </form>

                        {submissionStatus === 'success' ? (
                            <Card size="sm" className="mx-auto w-100 max-w-full">
                                <CardContent>Thanks for submitting your info. You can safely close this window.</CardContent>
                            </Card>
                        ) : null}
                    </>
                ) : null}
            </LayoutOnboarding>
        </div>
    );
}

export default EditClient;
